// slices/auth/register/thunk.ts
import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import "firebase/compat/firestore";

// action
import {
  registerUserSuccessful,
  registerUserFailed,
  resetRegisterFlagChange,
} from "./reducer";

// initialize relevant method of both Auth
const fireBaseBackend: any = getFirebaseBackend();

// Move interface out of the main execution scope
export interface RegisterUserData {
  email: string;
  password: string;
  first_name: string;
}

export const registerUser =
  (user: RegisterUserData) => async (dispatch: any) => {
    try {
      let response;

      if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
        const fireBaseBackend = getFirebaseBackend();

        if (!fireBaseBackend) {
          console.error("Firebase backend initialization failed");
          dispatch(registerUserFailed("Firebase backend not initialized"));
          return;
        }

        try {
          console.log("Attempting to register user:", {
            ...user,
            password: "***",
          });

          // Validate user data first
          if (!validateUserData(user)) {
            const error = "Invalid user data provided";
            console.error(error);
            dispatch(registerUserFailed(error));
            return;
          }

          response = await fireBaseBackend.registerUser(
            user.email,
            user.password,
            user.first_name
          );

          console.log("Registration response:", response);

          if (response) {
            // Convert Firebase User object to serializable data
            const userData = {
              uid: response.uid,
              email: response.email,
              displayName: response.displayName,
              emailVerified: response.emailVerified,
              createdAt: response.metadata?.creationTime,
              lastSignInTime: response.metadata?.lastSignInTime
            };
            dispatch(registerUserSuccessful(userData));
          } else {
            console.error("No response from registration");
            dispatch(registerUserFailed("No response from registration"));
            return;
          }
        } catch (error: any) {
          console.error("Firebase registration error:", error);
          const errorMessage = handleRegistrationError(error);
          dispatch(registerUserFailed(errorMessage));
          return;
        }
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      const errorMessage =
        typeof error === "string" ? error : handleRegistrationError(error);
      dispatch(registerUserFailed(errorMessage));
    }
  };

export const resetRegisterFlag = () => {
  try {
    const response = resetRegisterFlagChange();
    return response;
  } catch (error) {
    return error;
  }
};

// Helper function to handle registration errors
export const handleRegistrationError = (error: any): string => {
  let errorMessage = "Registration failed";

  if (error.code) {
    switch (error.code) {
      case "auth/email-already-in-use":
        errorMessage = "This email address is already registered. Please use a different email or try logging in.";
        break;
      case "auth/invalid-email":
        errorMessage = "Invalid email address";
        break;
      case "auth/operation-not-allowed":
        errorMessage = "Email/password registration is not enabled";
        break;
      case "auth/weak-password":
        errorMessage = "Password is too weak";
        break;
      default:
        errorMessage = error.message || "Registration failed";
    }
  } else if (error.message) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  return errorMessage;
};

// Helper function to validate user data
export const validateUserData = (userData: RegisterUserData): boolean => {
  if (!userData.email || !userData.password || !userData.first_name) {
    return false;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(userData.email)) {
    return false;
  }

  if (userData.password.length < 6) {
    return false;
  }

  if (userData.first_name.trim().length === 0) {
    return false;
  }

  return true;
};